import { immutableContext } from '@/analytics/context';
import type { CriteriaDimension, EntityNamespace, EntityReference } from '@/analytics/contract-v2';
import { filterSnapshot, type FilterMetadata } from '@/analytics/features/filters';
import { getClientProductDisplayText } from '@/utils/clientProductDisplay';

export type MatchStatus = 'matched' | 'not_matched' | 'unknown';

/** Only real primitive identifiers and display names enter the shared contract. */
export function researchEntity(
  namespace: EntityNamespace,
  id: unknown,
  name?: unknown,
): EntityReference | undefined {
  if (typeof id !== 'string' && typeof id !== 'number') return;
  if (typeof id === 'number' && (!Number.isSafeInteger(id) || id <= 0)) return;
  const value = String(id).trim();
  if (!/^[A-Za-z0-9_.:-]{1,128}$/.test(value)) return;
  const label =
    typeof name === 'string'
      ? name
          .replace(/[\u0000-\u001f]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .slice(0, 256)
      : '';
  return immutableContext({
    id: value,
    namespace,
    ...(label ? { name: label, label_source: 'loaded_entity' as const } : {}),
    label_resolution_status: label ? ('resolved' as const) : ('unresolved' as const),
  });
}

/** Bound approved match fields through T5; never project the product/provider record. */
export function productMatchEvidence(product: any): {
  dimensions: readonly CriteriaDimension[];
  status: MatchStatus;
  truncated: boolean;
} {
  const arrays = (key: string): any[] => (Array.isArray(product?.[key]) ? product[key] : []);
  const ingredients = [...arrays('ingredients'), ...arrays('matched_ingredients')];
  const clients = arrays('matched_client_products');
  const categories = arrays('matched_client_categories');
  const groups = arrays('matched_category_groups');
  const values = {
    ingredient_ids: [
      ...arrays('matched_ingredient_ids'),
      ...arrays('matched_ingredients').map((item) => item?.id),
      ...clients.flatMap((item) =>
        Array.isArray(item?.ingredient_ids) ? item.ingredient_ids : [],
      ),
    ],
    client_product_ids: clients.map((item) => item?.id),
    client_category_ids: categories.map((item) => item?.id),
    client_group_ids: groups.map((item) => item?.group_id),
    packaging_type_aliases: arrays('matched_packaging_types').filter(
      (value) => typeof value === 'string',
    ),
  };
  const labels = (items: any[], idKey = 'id', nameKey = 'name') =>
    Object.fromEntries(
      items
        .filter((item) => item && typeof item[nameKey] === 'string')
        .map((item) => [String(item[idKey]), item[nameKey]]),
    );
  const metadata: FilterMetadata = {
    provenance: Object.fromEntries(Object.keys(values).map((key) => [key, 'portfolio' as const])),
    labels: {
      ingredient_ids: labels(ingredients),
      client_product_ids: labels(
        clients.map((item) => ({
          id: item?.id,
          name:
            typeof item?.name === 'string' || typeof item?.client_alias === 'string'
              ? getClientProductDisplayText({
                  id: item?.id,
                  name: typeof item?.name === 'string' ? item.name : '',
                  client_alias: typeof item?.client_alias === 'string' ? item.client_alias : '',
                })
              : undefined,
        })),
      ),
      client_category_ids: labels(categories),
      client_group_ids: labels(groups, 'group_id', 'group_name'),
    },
  };
  const snapshot = filterSnapshot(values, metadata);
  const dimensions = snapshot.dimensions.filter((dimension) => dimension.active);
  // Material names have no trustworthy IDs in this response. Keep them as names,
  // never manufacture packaging IDs from a label or merge them into type aliases.
  const allMaterials = [
    ...new Set(
      arrays('matched_packaging_materials')
        .filter((value): value is string => typeof value === 'string')
        .map((value) =>
          value
            .replace(/[\u0000-\u001f]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim(),
        )
        .filter(Boolean),
    ),
  ];
  const materials = allMaterials.slice(0, 8).map((value) => value.slice(0, 80));
  if (materials.length)
    dimensions.push({
      key: 'packaging_material_names',
      value: materials,
      active: true,
      operator: 'in',
      provenance: 'portfolio',
    });
  return {
    dimensions: dimensions.map((dimension) => ({
      ...dimension,
      ...(dimension.entities
        ? {
            entities: dimension.entities.map((entity) =>
              entity.name ? { ...entity, label_source: 'loaded_entity' as const } : entity,
            ),
          }
        : {}),
    })),
    status:
      product?.is_match === true
        ? 'matched'
        : product?.is_match === false
        ? 'not_matched'
        : 'unknown',
    truncated:
      snapshot.truncated === true ||
      allMaterials.length > materials.length ||
      allMaterials.some((value) => value.length > 80),
  };
}
